import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  CreditCardIcon,
  FileTextIcon,
  SettingsIcon,
  BarChartIcon,
  DollarSignIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
} from "lucide-react";
import ModelPricing from "./ModelPricing";

interface BillingOverviewProps {
  onTabChange: (key: string) => void;
}

const BillingOverview: React.FC<BillingOverviewProps> = ({ onTabChange }) => {
  const quickActions = [
    {
      title: "Payment methods",
      description: "Add or change payment method",
      icon: <CreditCardIcon className="w-6 h-6" />,
      onClick: () => onTabChange("paymentMethods"),
      enabled: true,
    },
    {
      title: "Billing history",
      description: "View past and current invoices",
      icon: <FileTextIcon className="w-6 h-6" />,
      onClick: () => onTabChange("history"),
      enabled: true,
    },
    {
      title: "Preferences",
      description: "Manage billing information",
      icon: <SettingsIcon className="w-6 h-6" />,
      onClick: () => onTabChange("preferences"),
      enabled: true,
    },
    {
      title: "Usage limits",
      description: "Set monthly spend limits (coming soon)",
      icon: <BarChartIcon className="w-6 h-6" />,
      onClick: () => {},
      enabled: false,
    },
    {
      title: "Pricing",
      description: "View pricing and FAQs",
      icon: <DollarSignIcon className="w-6 h-6" />,
      onClick: () => window.open("https://trymaitai.ai/pricing", "_blank"),
      enabled: true,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Account Overview */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-gc-demi-bold text-foreground">Pay as you go</h2>
        </div>

        {/* Credit Balance */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground font-gc-reg">Credit balance</p>
              <p className="text-3xl font-gc-demi-bold text-foreground">$0.00</p>
            </div>
          </CardContent>
        </Card>

        {/* Invoicing Status */}
        <Card className="border-border bg-accent/10">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-maitai-green mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-gc-med text-foreground">Invoicing is on</p>
                <p className="text-sm text-muted-foreground font-gc-reg">
                  You will receive an invoice for each billing period. The card
                  on file will automatically be charged for the invoice amount
                  on the 1st of each month.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Quick Actions</h3>
          <p className="text-sm text-muted-foreground">Manage your billing settings and preferences</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className={`border-border transition-colors ${
                action.enabled
                  ? "cursor-pointer hover:bg-accent/20"
                  : "opacity-50 cursor-not-allowed"
              }`}
              onClick={action.enabled ? action.onClick : undefined}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`${action.enabled ? "text-maitai-green" : "text-muted-foreground"}`}>
                    {action.icon}
                  </div>
                  <div className="space-y-1 flex-1">
                    <p className={`text-sm font-gc-med ${action.enabled ? "text-foreground" : "text-muted-foreground"}`}>
                      {action.title}
                    </p>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      {action.description}
                    </p>
                    {action.title === "Pricing" && action.enabled && (
                      <ExternalLinkIcon className="w-3 h-3 text-muted-foreground mt-1" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Model Pricing */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Model Pricing</h3>
          <p className="text-sm text-muted-foreground">Current pricing for all available models</p>
        </div>
        <ModelPricing />
      </div>
    </div>
  );
};

export default BillingOverview;
